import { Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import { cn } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useKorintModuleConfig } from 'korint-sdk/react';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export function PolicyLifecycleSummaryModule() {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const policyLifecycleSummaryModuleConfig = useKorintModuleConfig(
    'policyLifecycleSummaryModuleConfig',
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('policyLifecycleSummary.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full md:w-1/3">
          <tbody>
            {policy.suspendedAt && (
              <tr>
                <td className="p-2 font-semibold text-sm w-1/2">
                  {t('policyLifecycleSummary.suspensionDate')}
                </td>
                <td className="w-1/2">
                  {policy?.suspendedAt
                    ? dayjs(policy.suspendedAt)
                        .tz('Europe/Paris')
                        .format(policyLifecycleSummaryModuleConfig.dateDisplayFormat)
                    : t('common.unavailable')}
                </td>
              </tr>
            )}
            <tr>
              <td
                className={cn(
                  'p-2 font-semibold text-sm w-1/2',
                  policy?.suspendedAt && 'text-muted-foreground',
                )}
              >
                {t('policyLifecycleSummary.effectiveDate')}
              </td>
              <td className={`w-1/2 ${policy?.suspendedAt ? 'text-muted-foreground' : ''}`}>
                {policy?.startedAt
                  ? dayjs(policy.startedAt)
                      .tz('Europe/Paris')
                      .format(policyLifecycleSummaryModuleConfig.dateDisplayFormat)
                  : t('common.unavailable')}
              </td>
            </tr>
            <tr>
              <td
                className={cn(
                  'p-2 font-semibold text-sm w-1/2',
                  policy?.suspendedAt && 'text-muted-foreground',
                )}
              >
                {t('policyLifecycleSummary.expiryDate')}
              </td>
              <td className={cn('w-1/2', policy?.suspendedAt && 'text-muted-foreground')}>
                {policy?.nextRenewalAt
                  ? dayjs(policy.nextRenewalAt)
                      .tz('Europe/Paris')
                      .subtract(1, 'millisecond')
                      .format(policyLifecycleSummaryModuleConfig.dateDisplayFormat)
                  : t('common.unavailable')}
              </td>
            </tr>
            {policy.stop && (
              <>
                {policy.stop.reason && (
                  <tr>
                    <td className="p-2 font-semibold text-sm w-1/2">
                      {t('policyLifecycle.terminationReason')}
                    </td>
                    <td className="w-1/2">
                      {t([
                        `${policy.productId}.stopReasons.${policy.stop.reason}`,
                        `stopReasons.${policy.stop.reason}`,
                      ])}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="p-2 font-semibold text-sm w-1/2">
                    {t('policyLifecycle.terminationRequestDate')}
                  </td>
                  <td className="w-1/2">
                    {policy.stop.createdAt
                      ? dayjs(policy.stop.createdAt)
                          .tz('Europe/Paris')
                          .format(policyLifecycleSummaryModuleConfig.dateDisplayFormat)
                      : t('common.unavailable')}
                  </td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold text-sm w-1/2">
                    {t('policyLifecycle.terminationEffectiveDate')}
                  </td>
                  <td className="w-1/2">
                    {(policy.stoppedAt ?? policy.stop.stopDate)
                      ? dayjs(policy.stoppedAt ?? policy.stop.stopDate)
                          .tz('Europe/Paris')
                          .format(policyLifecycleSummaryModuleConfig.dateDisplayFormat)
                      : t('common.unavailable')}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
